import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";

import { LoginService } from "src/app/shared/services/login/login.service";
import { LocalesService } from "src/app/shared/services/locales/locales.service";
import * as LoginActionTypes from "../../actions/login/login.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import * as UserActionTypes from "../../actions/users/user.actions";
import * as MenuActionTypes from "../../actions/menus/menu.actions";
import { AppState } from "../../reducers";
import { getCurrentTenantFormatList } from "../../actions/tenant/tenant.actions";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class LoginEffects {
  authenticate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.authenticate),
      exhaustMap((action) =>
        this.loginService
          .logIn(action) // this.loginService.logIn(action)
          .pipe(
            map((userSession) => {
              this.store.dispatch(
                LoginActionTypes.loginSuccess({
                  authenticatedUser: userSession,
                })
              );
              this.store.dispatch(getCurrentTenantFormatList(null));
              return MenuActionTypes.getMenus();
            }),
            catchError((error) => of(LoginActionTypes.loginFailure({ error })))
          )
      )
    )
  );

  loginFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActionTypes.loginFailure),
        tap((error: any) =>
          this.store.dispatch(
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                error.error && (typeof error.error === 'string' || error.error instanceof String)
                  ? `App.Errors.${error.error}`
                  : "App.Errors.ErrorNoData",
                SnackbarType.ERROR
              )
            )
          )
        ),
        catchError((error) => of(LoginActionTypes.loginFailure({ error })))
      ),
    { dispatch: false }
  );

  authenticateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActionTypes.loginSuccess),
        tap((payload) => {
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem(
            "access_token",
            (payload as any).authenticatedUser.access_token
          );
          localStorage.setItem(
            "refresh_token",
            (payload as any).authenticatedUser.refresh_token
          );
          localStorage.setItem(
            "tenant_token",
            (payload as any).authenticatedUser.tenant_token
          );
          localStorage.setItem(
            "tenant_country_code",
            (payload as any).authenticatedUser.tenant_country_code
          );
          localStorage.setItem(
            "entity_code",
            (payload as any).authenticatedUser.entity_code
          );
          localStorage.setItem(
            "locale",
            (payload as any).authenticatedUser.locale
          );
          localStorage.setItem(
            "localeCode",
            ((payload as any).authenticatedUser.locale as string).split("-")[0]
          );
          this.translate.use(
            ((payload as any).authenticatedUser.locale as string).split("-")[0]
          );
          this.store.dispatch(
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj("App.Success", SnackbarType.SUCCESS)
            )
          );
          this.store.dispatch(getCurrentTenantFormatList(null));
          this.store.dispatch(
            UserActionTypes.getLoggedUser({
              user: localStorage.getItem("username"),
            })
          );
          this.store.dispatch(MessagesActionTypes.setMessageFlag());
          this.router.navigateByUrl("/");
        }),
        catchError((error) => of(LoginActionTypes.loginFailure({ error })))
      ),
    { dispatch: false }
  );

  logOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.logOut),
      exhaustMap((action) =>
      from(this.router.navigate(['/login'])).pipe(
      map(() => LoginActionTypes.logOutSuccess({ isPasswordRecovery: false })),
      catchError((error) => 
        {
          const actions = [
            LoginActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "Auth.LogoutError",
                SnackbarType.ERROR
              )
            )
          ];
          return from(actions);
        })),
      )
    )
  );

  logOutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.logOutSuccess),
      exhaustMap((action) => this.loginService.logOutSuccess(action))
    )
  );

  recoverPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.recoverPassword),
      exhaustMap((action) =>
        this.loginService.recoverPassword(action.passwordRecovery).pipe(
          mergeMap(() => [
            LoginActionTypes.setLoadingEndState(),
            LoginActionTypes.recoverPasswordSuccess(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "Auth.PasswordRecoverySuccess",
                SnackbarType.SUCCESS
              )
            )
          ]),
          catchError((error) => {
            const actions = [
              LoginActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Auth.PasswordRecoveryError",
                  SnackbarType.ERROR
                )
              )
            ];
            return from(actions);
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.resetPassword),
      exhaustMap((action) =>
        this.loginService.resetPassword(action).pipe(
          mergeMap(() => [
            LoginActionTypes.setLoadingEndState(),
            LoginActionTypes.resetPasswordSuccess(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "Auth.PasswordChangeSuccess",
                SnackbarType.SUCCESS
              )
            )
          ]),
          catchError((error) => {
            const actions = [
              LoginActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Auth.PasswordChangeError",
                  SnackbarType.ERROR
                )
              )
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLocales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionTypes.getLocales),
      exhaustMap((action) =>
        this.localesService.getLocales(action.options).pipe(
          map((result) => LoginActionTypes.loadLocales({ response: result })),
          catchError((error) => {
            const actions = [
              LoginActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorGetLocales",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private loginService: LoginService,
    private localesService: LocalesService,
    private translate: TranslateService,
    private router: Router
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
